import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { AppBar, Button, CircularProgress, Typography } from '@material-ui/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import { PermissionBox } from '../../components/PermissionBox'
import { PERMISSIONS_QUERY } from '../../graphql/permissions'
import {
  CREATE_ROLE_MUTATION,
  DELETE_ROLE_MUTATION,
  ROLE_MANAGEMENT_SCOPE_QUERY,
  ROLE_QUERY,
  UPDATE_ROLE_MUTATION
} from '../../graphql/role'
import { getTranslate } from '../../localization'
import { Permission, RoleDetails, RoleManagementScope } from '../../types/role'
import RoleBasicInfo from './roleBasicInfo'
import {
  applyGrantableScope,
  canManageRole,
  creatableEntityTypes,
  readOnlyPermissions,
  selectedPermissionIDs
} from './roleScope'
import useStyles from './styles/index.styles'

function RoleManagementForm() {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ roleID: string }>()
  const [initialPermissions, setInitialPermissions] = useState<Permission[]>([])
  const [temporaryRoleDetails, setTemporaryRoleDetails] = useState<RoleDetails>(
    { name: '', description: '', isActive: true, roleID: '', permissions: [] }
  )

  const [filteredPermissions, setfilteredPermissions] = useState<Permission[]>(
    []
  )

  const [getRole, { data: roleDetailData, loading: loadingForRole }] =
    useLazyQuery<{
      role: RoleDetails
    }>(ROLE_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        setInitialPermissions(res.role.permissions)
        setTemporaryRoleDetails(res.role)
      }
    })

  const {} = useQuery<{
    permissions: Permission[]
  }>(PERMISSIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: { filters: {} },
    skip: !!params.roleID,
    onCompleted: (res) => {
      setTemporaryRoleDetails({
        ...temporaryRoleDetails,
        permissions: res.permissions
      })
    }
  })

  useEffect(() => {
    if (params.roleID) {
      getRole({ variables: { roleID: params.roleID } })
    }
  }, [])

  // What IAM lets this user create and grant. If it fails (an older backend), the form
  // behaves as before and IAM still refuses anything out of scope.
  const { data: scopeData } = useQuery<{
    roleManagementScope: RoleManagementScope
  }>(ROLE_MANAGEMENT_SCOPE_QUERY, { fetchPolicy: 'network-only' })
  const scope = scopeData?.roleManagementScope

  const readOnly =
    !!params.roleID && !canManageRole(roleDetailData?.role.canManage)

  // Apply the scope to the permission tree once, after both it and the tree have loaded.
  const scopeApplied = useRef(false)
  useEffect(() => {
    if (scopeApplied.current || temporaryRoleDetails.permissions.length === 0) {
      return
    }
    if (params.roleID && !roleDetailData) return
    if (readOnly) {
      scopeApplied.current = true
      setTemporaryRoleDetails({
        ...temporaryRoleDetails,
        permissions: readOnlyPermissions(temporaryRoleDetails.permissions)
      })
      return
    }
    if (!scope) return
    scopeApplied.current = true
    setTemporaryRoleDetails({
      ...temporaryRoleDetails,
      permissions: applyGrantableScope(
        temporaryRoleDetails.permissions,
        scope,
        selectedPermissionIDs(initialPermissions)
      )
    })
  }, [scope, readOnly, roleDetailData, temporaryRoleDetails.permissions])

  const [createRole, { loading: loadingForCreateRole }] = useMutation(
    CREATE_ROLE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        toast.success(res.createRole.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [deleteRole, { loading: loadingForDeleteRole }] = useMutation(
    DELETE_ROLE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        toast.success(res.deleteRole.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [updateRole, { loading: loadingForUpdateRole }] = useMutation(
    UPDATE_ROLE_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        toast.success(res.updateRole.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Role Management'),
        link: '/access-management/role-management'
      },
      {
        label: params.roleID
          ? roleDetailData?.role.name || ''
          : getTranslate('Create New Role')
      }
    ],
    [roleDetailData]
  )

  return (
    <div className={classes.main}>
      <div className={classes.root}>
        <PageHeader title={getTranslate('Role Access')} />
        <AppBar position="static" color="transparent">
          <Breadcrumbs levels={cachedLevels} loading={loadingForRole} />
        </AppBar>
        <div className={classes.top}>
          <div className={classes.header}>
            <Typography variant="h3" gutterBottom>
              {getTranslate(params.roleID ? 'Edit Role' : 'Create New Role')}
            </Typography>
          </div>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {getTranslate('Role Basic Info')}
          </Typography>
          <RoleBasicInfo
            temporaryRoleDetails={temporaryRoleDetails}
            setTemporaryRoleDetails={setTemporaryRoleDetails}
            setfilteredPermissions={setfilteredPermissions}
            entityTypeOptions={creatableEntityTypes(scope)}
            readOnly={readOnly}
          />
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            {getTranslate('Permissions')}
          </Typography>
          <div className={classes.content}>
            {loadingForRole ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={36} />
              </div>
            ) : (
              filteredPermissions.map((permission) => (
                <PermissionBox
                  key={permission.permissionName}
                  permission={permission}
                  initialPermissions={initialPermissions}
                  temporaryPermissions={temporaryRoleDetails.permissions}
                  setTemporayPermissions={(p) => {
                    setTemporaryRoleDetails({
                      ...temporaryRoleDetails,
                      permissions: p
                    })
                  }}
                />
              ))
            )}
          </div>
        </div>
      </div>
      <div className={classes.footer}>
        <div>
          <Typography className={classes.title}>
            {getTranslate('Unsaved Changes')}
          </Typography>
          <Typography className={classes.subTitle}>
            {getTranslate('You have pending changes that have not been saved')}
          </Typography>
        </div>
        <div className={classes.btnContainer}>
          {params.roleID && !readOnly && (
            <Button
              variant="text"
              color="primary"
              disabled={
                loadingForUpdateRole ||
                loadingForCreateRole ||
                loadingForDeleteRole
              }
              onClick={() => {
                deleteRole({ variables: { roleID: params.roleID } })
              }}>
              <Typography className={classes.redText}>
                {getTranslate('Delete Role')}
                {loadingForDeleteRole && (
                  <CircularProgress
                    className={classes.circularProgressDelete}
                    size={16}
                  />
                )}
              </Typography>
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            className={classes.cancelBtn}
            onClick={() => {
              history.goBack()
            }}>
            <Typography className={classes.redText}>
              {getTranslate('Cancel')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            hidden={readOnly}
            disabled={
              loadingForUpdateRole ||
              loadingForCreateRole ||
              loadingForDeleteRole ||
              temporaryRoleDetails.name === '' ||
              temporaryRoleDetails.description === ''
            }
            onClick={() => {
              if (params.roleID) {
                updateRole({
                  variables: {
                    input: {
                      name: temporaryRoleDetails.name,
                      description: temporaryRoleDetails.description,
                      isActive: temporaryRoleDetails.isActive,
                      roleID: temporaryRoleDetails.roleID,
                      permissionIDs: temporaryRoleDetails.permissions.flatMap(
                        (perm) =>
                          perm.subPermissions.flatMap((subr) =>
                            subr.actions
                              .filter((act) => act.selected)
                              .map((act) => act.permissionID)
                          )
                      )
                    }
                  }
                })
              } else {
                createRole({
                  variables: {
                    input: {
                      name: temporaryRoleDetails.name,
                      description: temporaryRoleDetails.description,
                      isActive: temporaryRoleDetails.isActive,
                      entityType: temporaryRoleDetails.entityType,
                      permissionIDs: temporaryRoleDetails.permissions
                        .flatMap((perm) => perm.subPermissions)
                        .flatMap((sub) => sub.actions)
                        .filter((action) => action.selected)
                        .map((action) => action.permissionID)
                    }
                  }
                })
              }
            }}>
            <Typography className={classes.whiteText}>
              {getTranslate(params.roleID ? 'Save Changes' : 'Create Role')}
              {(loadingForUpdateRole || loadingForCreateRole) && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                />
              )}
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default RoleManagementForm
